import { cancel, put, retry, select } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';
import {
	setComponentLoading,
	setComponentErrors,
	setKs4HasBeenChecked,
	setErrorCode,
} from '../../ks5PerformanceMeasures/singleSchool/slice';
import { sagaFlowConfigurations } from '../../../../../../constants';
import { getBest3SummaryProfileDataBySchoolId } from '../../../../../../api/groupsAPI';
import { getActiveChildSchool, getGroupAccessId } from 'features/app/redux/context';
import { setMainGridDataSuccess } from '../../ks5PerformanceMeasures/singleSchool/slice';
import { getGroupAccessRecords } from 'features/groups/setup/redux';
import { getKs4HasBeenChecked } from '../../ks5PerformanceMeasures/singleSchool/selectors';
import { getGridDataForGridId } from '../../../../../../sharedSelectors';
import { translate } from '../../../../../../utils/locale';
/**
 * Get the best 3 summary profile grid data
 * @returns {SagaIterator<void>}
 */
export function* getBest3SummaryProfileGridFlow(): SagaIterator<void> {
	//get the school id
	const schoolId = yield select(getActiveChildSchool);

	if (!schoolId) {
		//bail out of the saga
		yield cancel();
	}
	//get the data from the store
	const initialData = yield select(
		getGridDataForGridId(
			'matSingleSchoolPerformanceMeasures',
			`0-${schoolId.childSchoolId},BestThreeSummaryProfile`
		)
	);
	//clear the errors for this component
	yield put(setComponentErrors({ component: 'best3SummaryProfileGrid', value: undefined }));

	//set the component loading state to true
	yield put(setComponentLoading({ component: 'best3SummaryProfileGrid', value: true }));

	//get the group access id
	const groupAccessId = yield select(getGroupAccessId);

	//if the group access id is 0, bail out of the saga
	if (groupAccessId === 0) {
		//set the errors for this component
		yield put(
			setComponentErrors({
				component: 'best3SummaryProfileGrid',
				value: translate('groups.ks5Pm.common.NO_ACCESS_ID') as string,
			})
		);
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'best3SummaryProfileGrid', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//get the access records (available key stages)
	const accessRecords = yield select(getGroupAccessRecords);

	//check the access record for the the current group access id for KS4
	const isKs4 = accessRecords?.find((x) => x.id === groupAccessId).examLevel === 32;

	//add in additional check for the state of KS4 having already been set
	const ks4HasBeenChecked = yield select(getKs4HasBeenChecked);

	//if the access record is for KS4, check to see if the state of KS4 has already been set
	if (isKs4) {
		//if the state of KS4 has not been set, set it to true
		if (!ks4HasBeenChecked) {
			yield put(setKs4HasBeenChecked(true));
		}
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'best3SummaryProfileGrid', value: false }));

		//bail out of the saga
		yield cancel();
	}

	//clear the state of KS4
	yield put(setKs4HasBeenChecked(false));

	//check to see if the data is already in the store
	if (initialData !== undefined) {
		yield put(setComponentLoading({ component: 'best3SummaryProfileGrid', value: false }));
		yield cancel();
	}

	//get the data from the api
	const best3SummaryProfileData = yield retry(
		sagaFlowConfigurations.apiRetry,
		sagaFlowConfigurations.milliSeconds,
		getBest3SummaryProfileDataBySchoolId,
		groupAccessId,
		schoolId.childSchoolId
	);

	//if there is an error, set the errors for this component
	if (best3SummaryProfileData.hasError) {
		//if the error code is 204, set the errors for this component
		if (best3SummaryProfileData.errors[0].errorCode === 204) {
			yield put(
				setComponentErrors({
					component: 'best3SummaryProfileGrid',
					value: ' ',
				})
			);

			//set the errors for this component
			yield put(
				setErrorCode({
					component: 'best3SummaryProfileGrid',
					value: 204,
				})
			);
			//set the component loading state to false
			yield put(setComponentLoading({ component: 'best3SummaryProfileGrid', value: false }));

			yield cancel();
		}
		//set the errors for this component
		yield put(
			setComponentErrors({
				component: 'best3SummaryProfileGrid',
				value: translate('groups.ks5Pm.common.MESSAGE') as string,
			})
		);
		//set the component loading state to false
		yield put(setComponentLoading({ component: 'best3SummaryProfileGrid', value: false }));
		//bail out of the saga
		yield cancel();
	}

	//set the data in the store
	yield put(setMainGridDataSuccess(best3SummaryProfileData.responseObject));

	//set the component loading state to false
	yield put(setComponentLoading({ component: 'best3SummaryProfileGrid', value: false }));

	//Bail out of the saga
	yield cancel();
}
